import { DragListener, RnaComplexProps, FullKeys, FullKeysRecord } from "../../../App";
import { Tab } from "../../../app_data/Tab";
import { AppSpecificOrientationEditor } from "../../../components/app_specific/editors/AppSpecificOrientationEditor";
import { BasePairsEditor } from "../../../components/app_specific/editors/BasePairsEditor";
import { NucleotideRegionsAnnotateMenu } from "../../../components/app_specific/menus/annotate_menus/NucleotideRegionsAnnotateMenu";
import { NucleotideKeysToRerender, BasePairKeysToRerender } from "../../../context/Context";
import Color, { BLACK, DEFAULT_ALPHA, areEqual } from "../../../data_structures/Color";
import { Vector2D, add } from "../../../data_structures/Vector2D";
import { parseInteger } from "../../../utils/Utils";
import { AbstractInteractionConstraint, InteractionConstraintError } from "../AbstractInteractionConstraint";
import { linearDrag } from "../CommonDragListeners";
import { Helix, InteractionConstraint, iterateOverFreeNucleotidesAndHelicesPerScene } from "../InteractionConstraints";
import { AllInOneEditor } from "./AllInOneEditor";

export class SingleColorInteractionConstraint extends AbstractInteractionConstraint {
  private readonly dragListener : DragListener;
  private readonly editMenuProps : AppSpecificOrientationEditor.Props;
  private readonly color : Color;
  private readonly initialBasePairs : BasePairsEditor.InitialBasePairs;
  private readonly approveBasePair : (basePair : BasePairsEditor.BasePair) => boolean;
  private readonly fullKeysOfNucleotidesWithColor : Array<FullKeys>;
  private readonly helices : Array<Helix>;

  public constructor(
    rnaComplexProps : RnaComplexProps,
    setNucleotideKeysToRerender : (nucleotideKeysToRerender : NucleotideKeysToRerender) => void,
    setBasePairKeysToRerender : (basePairKeysToRerender : BasePairKeysToRerender) => void,
    setDebugVisualElements : (debugVisualElements : Array<JSX.Element>) => void,
    tab : Tab,
    indicesOfFrozenNucleotides : FullKeysRecord,
    { treatNoncanonicalBasePairsAsUnpairedFlag } : InteractionConstraint.Options,
    fullKeys0 : FullKeys,
    fullKeys1? : FullKeys,
  ) {
    super(
      rnaComplexProps,
      setNucleotideKeysToRerender,
      setBasePairKeysToRerender,
      setDebugVisualElements,
      indicesOfFrozenNucleotides,
      fullKeys0,
      fullKeys1
    );
    const {
      rnaComplexIndex,
      rnaMoleculeName,
      nucleotideIndex
    } = this.fullKeys0;
    const allNucleotides = new Array<Vector2D>();
    const nucleotideKeysToRerender : NucleotideKeysToRerender = {};
    const basePairKeysToRerender : BasePairKeysToRerender = {};

    const singularRnaComplexProps = this.rnaComplexProps[rnaComplexIndex];
    const singularRnaMoleculeProps = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName];
    const singularNucleotideProps = singularRnaMoleculeProps.nucleotideProps[nucleotideIndex];
    const color = singularNucleotideProps.color ?? BLACK;
    this.color = color;

    this.fullKeysOfNucleotidesWithColor = [];
    const rnaComplexIndices = Object.keys(this.rnaComplexProps).map(parseInteger);
    for (let rnaComplexIndex of rnaComplexIndices) {
      const singularRnaComplexProps = this.rnaComplexProps[rnaComplexIndex];
      nucleotideKeysToRerender[rnaComplexIndex] = {};
      const nucleotideKeysToRerenderPerRnaComplex = nucleotideKeysToRerender[rnaComplexIndex];
      basePairKeysToRerender[rnaComplexIndex] = [];
      const basePairKeysToRerenderPerRnaComplex = basePairKeysToRerender[rnaComplexIndex];
      const basePairsPerRnaComplex = singularRnaComplexProps.basePairs;
      const rnaMoleculeNames = Object.keys(singularRnaComplexProps.rnaMoleculeProps);
      for (let rnaMoleculeName of rnaMoleculeNames) {
        const singularRnaMoleculeProps = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName];
        nucleotideKeysToRerenderPerRnaComplex[rnaMoleculeName] = [];
        const nucleotideKeysToRerenderPerRnaMolecule = nucleotideKeysToRerenderPerRnaComplex[rnaMoleculeName];
        const basePairsPerRnaMolecule = basePairsPerRnaComplex[rnaMoleculeName];
        const nucleotideIndices = Object.keys(singularRnaMoleculeProps.nucleotideProps).map(parseInteger);
        for (let nucleotideIndex of nucleotideIndices) {
          const singularNucleotideProps = singularRnaMoleculeProps.nucleotideProps[nucleotideIndex];
          const nucleotideColor = singularNucleotideProps.color ?? BLACK;
          if (areEqual(
            color,
            nucleotideColor
          )) {
            let frozenFlag = false;
            if (rnaComplexIndex in indicesOfFrozenNucleotides) {
              const indicesOfFrozenNucleotidesPerRnaComplex = indicesOfFrozenNucleotides[rnaComplexIndex];
              if (rnaMoleculeName in indicesOfFrozenNucleotidesPerRnaComplex) {
                const indicesOfFrozenNucleotidesPerRnaComplexPerRnaMolecule = indicesOfFrozenNucleotidesPerRnaComplex[rnaMoleculeName];
                if (indicesOfFrozenNucleotidesPerRnaComplexPerRnaMolecule.has(nucleotideIndex)) {
                  frozenFlag = true;
                }
              }
            }
            this.fullKeysOfNucleotidesWithColor.push({
              rnaComplexIndex,
              rnaMoleculeName,
              nucleotideIndex
            });
            nucleotideKeysToRerenderPerRnaMolecule.push(nucleotideIndex);
            if (!frozenFlag) {
              allNucleotides.push(singularNucleotideProps);

              if (nucleotideIndex in basePairsPerRnaMolecule) {
                // const mappedBasePairInformation = basePairsPerRnaMolecule[nucleotideIndex];
                basePairKeysToRerenderPerRnaComplex.push({
                  rnaMoleculeName,
                  nucleotideIndex
                });
                // const singularBasePairedRnaMoleculeProps = singularRnaComplexProps.rnaMoleculeProps[mappedBasePairInformation.rnaMoleculeName];
                // const singularBasePairedNucleotideProps = singularBasePairedRnaMoleculeProps.nucleotideProps[mappedBasePairInformation.nucleotideIndex];
                // const mismatchedColorBasePairExistsFlag = !areEqual(
                //   color,
                //   singularBasePairedNucleotideProps.color ?? BLACK
                // );
                // if (mismatchedColorBasePairExistsFlag && tab !== Tab.ANNOTATE) {
                //   this.basePairBetweenDifferentColorsError = "Cannot interact with pairs of differently colored base-paired nucleotides using this selection constraint.";
                // }
              }
            }
          }
        }
      }
    }
    function rerender() {
      setNucleotideKeysToRerender(structuredClone(nucleotideKeysToRerender));
      setBasePairKeysToRerender(structuredClone(basePairKeysToRerender));
    }
    this.dragListener = linearDrag(
      structuredClone(singularNucleotideProps),
      allNucleotides,
      rerender
    );
    this.editMenuProps = {
      initialCenter : singularNucleotideProps,
      normal : {
        x : 1,
        y : 0
      },
      positions : allNucleotides,
      onUpdatePositions : rerender
    };
    const unfilteredInitialBasePairs : Array<BasePairsEditor.BasePair> = [];
    const helicesPerScene = iterateOverFreeNucleotidesAndHelicesPerScene(
      rnaComplexProps,
      treatNoncanonicalBasePairsAsUnpairedFlag
    );
    const unfilteredHelices = new Array<Helix>();
    helicesPerScene.forEach(function(helixDataPerRnaComplex) {
      const rnaComplexIndex = helixDataPerRnaComplex.rnaComplexIndex;
      const singularRnaComplexProps = rnaComplexProps[rnaComplexIndex];
      helixDataPerRnaComplex.helixDataPerRnaMolecules.forEach(function(helixDataPerRnaMolecule) {
        const rnaMoleculeName0 = helixDataPerRnaMolecule.rnaMoleculeName0;
        const singularRnaMoleculeProps0 = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName0];
        unfilteredHelices.push(...helixDataPerRnaMolecule.helixData.map(function({ rnaMoleculeName1, start, stop }) {
          return {
            rnaComplexIndex,
            rnaMoleculeName0,
            rnaMoleculeName1,
            start,
            stop
          };
        }));
      });
    });
    helicesPerScene.forEach(function(helixDataPerRnaComplex) {
      const rnaComplexIndex = helixDataPerRnaComplex.rnaComplexIndex;
      const singularRnaComplexProps = rnaComplexProps[rnaComplexIndex];
      helixDataPerRnaComplex.helixDataPerRnaMolecules.forEach(function(helixDataPerRnaMolecule) {
        const rnaMoleculeName0 = helixDataPerRnaMolecule.rnaMoleculeName0;
        const singularRnaMoleculeProps0 = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName0];
        unfilteredInitialBasePairs.push(...helixDataPerRnaMolecule.helixData.map(function(helixDatum) {
          const rnaMoleculeName1 = helixDatum.rnaMoleculeName1;
          const singularRnaMoleculeProps1 = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName1];
          return {
            rnaComplexIndex,
            rnaMoleculeName0,
            rnaMoleculeName1 : helixDatum.rnaMoleculeName1,
            nucleotideIndex0 : helixDatum.start[0] + singularRnaMoleculeProps0.firstNucleotideIndex,
            nucleotideIndex1 : Math.max(helixDatum.start[1], helixDatum.stop[1]) + singularRnaMoleculeProps1.firstNucleotideIndex,
            length : Math.abs(helixDatum.start[0] - helixDatum.stop[0]) + 1
          };
        }));
      });
    });
    function approveBasePair(basePair : BasePairsEditor.BasePair) {
      const singularRnaComplexProps = rnaComplexProps[basePair.rnaComplexIndex];
      const singularRnaMoleculeProps0 = singularRnaComplexProps.rnaMoleculeProps[basePair.rnaMoleculeName0];
      const singularRnaMoleculeProps1 = singularRnaComplexProps.rnaMoleculeProps[basePair.rnaMoleculeName1];

      const initialFormattedNucleotideIndex0 = basePair.nucleotideIndex0 - singularRnaMoleculeProps0.firstNucleotideIndex;
      const initialFormattedNucleotideIndex1 = basePair.nucleotideIndex1 - singularRnaMoleculeProps1.firstNucleotideIndex;
      for (let i = 0; i < basePair.length; i++) {
        const singularNucleotideProps0 = singularRnaMoleculeProps0.nucleotideProps[initialFormattedNucleotideIndex0 + i];
        const singularNucleotideProps1 = singularRnaMoleculeProps1.nucleotideProps[initialFormattedNucleotideIndex1 - i];
        if (!areEqual(
          color,
          singularNucleotideProps0.color ?? BLACK
        )) {
          return false;
        }
        if (!areEqual(
          color,
          singularNucleotideProps1.color ?? BLACK
        )) {
          return false;
        }
      }
      return true;
    }
    this.approveBasePair = approveBasePair;
    const filteredInitialBasePairs = unfilteredInitialBasePairs.filter(approveBasePair);
    this.initialBasePairs = filteredInitialBasePairs;
    this.addFullIndicesPerNucleotideKeysToRerender(nucleotideKeysToRerender);
    this.helices = unfilteredHelices.filter(({ rnaComplexIndex, rnaMoleculeName0, rnaMoleculeName1, start, stop }) => {
      const singularRnaComplexProps = rnaComplexProps[rnaComplexIndex];
      const singularRnaMoleculeProps0 = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName0];
      const singularRnaMoleculeProps1 = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName1];
      const length = Math.abs(stop[0] - start[0]) + 1
      for (let i = 0; i < length; i++) {
        const singularNucleotideProps0 = singularRnaMoleculeProps0.nucleotideProps[start[0] + i];
        const singularNucleotideProps1 = singularRnaMoleculeProps1.nucleotideProps[start[1] + i];
        return (
          areEqual(
            color,
            singularNucleotideProps0.color ?? BLACK
          ) &&
          areEqual(
            color,
            singularNucleotideProps1.color ?? BLACK
          )
        );
      }
      return false;
    });
  }

  public override drag() {
    return this.dragListener;
  }

  public override createRightClickMenu(tab : InteractionConstraint.SupportedTab) {
    const color = this.color;
    const approveBasePair = this.approveBasePair;
    let menu : JSX.Element;
    switch (tab) {
      case Tab.EDIT : {
        menu = <AllInOneEditor.Component
          {...this.editMenuProps}
        />;
        break;
      }
      case Tab.FORMAT : {
        menu = <BasePairsEditor.Component
          rnaComplexProps = {this.rnaComplexProps}
          initialBasePairs = {this.initialBasePairs}
          approveBasePairs = {function(basePairs) {
            // Do nothing.
          }}
        />;
        break;
      }
      case Tab.ANNOTATE : {
        const regions : NucleotideRegionsAnnotateMenu.Regions = {};
        for (const {
          rnaComplexIndex,
          rnaMoleculeName,
          nucleotideIndex
        } of this.fullKeysOfNucleotidesWithColor) {
          if (!(rnaComplexIndex in regions)) {
            regions[rnaComplexIndex] = {};
          }
          const regionsPerRnaComplex = regions[rnaComplexIndex];
          if (!(rnaMoleculeName in regionsPerRnaComplex)) {
            regionsPerRnaComplex[rnaMoleculeName] = [];
          }
          const regionsPerRnaMolecule = regionsPerRnaComplex[rnaMoleculeName];
          if (regionsPerRnaMolecule.length > 0) {
            const previouslyAddedRegion = regionsPerRnaMolecule[regionsPerRnaMolecule.length - 1];
            if (nucleotideIndex === previouslyAddedRegion.maximumNucleotideIndexInclusive + 1) {
              previouslyAddedRegion.maximumNucleotideIndexInclusive = nucleotideIndex;
            } else if (nucleotideIndex === previouslyAddedRegion.minimumNucleotideIndexInclusive - 1) {
              previouslyAddedRegion.minimumNucleotideIndexInclusive = nucleotideIndex;
            } else {
              regionsPerRnaMolecule.push({
                minimumNucleotideIndexInclusive : nucleotideIndex,
                maximumNucleotideIndexInclusive : nucleotideIndex
              });
            }
          } else {
            regionsPerRnaMolecule.push({
              minimumNucleotideIndexInclusive : nucleotideIndex,
              maximumNucleotideIndexInclusive : nucleotideIndex
            });
          }
        }
        menu = <NucleotideRegionsAnnotateMenu.Component
          regions = {regions}
          rnaComplexProps = {this.rnaComplexProps}
          setNucleotideKeysToRerender = {this.setNucleotideKeysToRerender}
        />;
        break;
      }
      default : {
        throw "Unhandled switch case.";
      }
    }
    return <>
      <b>
        {tab} all nucleotides of the same color:
      </b>
      <br/>
      (red = {color.red}, green = {color.green}, blue = {color.blue}, alpha = {color.alpha ?? DEFAULT_ALPHA}):
      <br/>
      {menu}
    </>;
  }

  public override getHelices() {
    return this.helices;
  }

  public override constrainRelevantHelices(helices : Array<Helix>) {
    return helices.filter(({ rnaComplexIndex, rnaMoleculeName0, rnaMoleculeName1, start, stop }) => {
      const singularRnaComplexProps = this.rnaComplexProps[rnaComplexIndex];
      const singularRnaMoleculeProps0 = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName0];
      const singularRnaMoleculeProps1 = singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName1];
      const length = Math.abs(stop[0] - start[0]) + 1;
      const increment0 = Math.sign(stop[0] - start[0]);
      const increment1 = Math.sign(stop[1] - start[1]);
      let nucleotideIndex0 = start[0];
      let nucleotideIndex1 = start[1];
      for (let i = 0; i < length; i++) {
        const singularNucleotideProps0 = singularRnaMoleculeProps0.nucleotideProps[nucleotideIndex0];
        const singularNucleotideProps1 = singularRnaMoleculeProps1.nucleotideProps[nucleotideIndex1];
        const color0 = singularNucleotideProps0.color ?? BLACK;
        const color1 = singularNucleotideProps1.color ?? BLACK;

        if ((
          // If ANY nucleotide in the helix matches the expected color, approve the helix.
          areEqual(this.color, color0) ||
          areEqual(this.color, color1)
        )) {
          return true;
        }

        nucleotideIndex0 += increment0;
        nucleotideIndex1 += increment1;
      }
      return false;
    });
  }
}